<script setup lang="ts">
import { computed } from 'vue'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { MetricsSnapshot } from '@/models/MetricsSnapshot'

const props = withDefaults(
  defineProps<{
    snapshots: MetricsSnapshot[]
    loading: boolean
    visible: boolean
  }>(),
  {
    loading: false,
    visible: true,
  },
)

interface BreakdownItem {
  state: string
  label: string
  totalCount: number
  colorClass: string
}

const breakdownItems = computed<BreakdownItem[]>(() => {
  const tenantSnapshots = props.snapshots.filter((s) => s.ownerId === '__tenant__')

  const states = [
    { state: 'active', label: 'Active', colorClass: 'text-green-600 dark:text-green-400' },
    { state: 'archived', label: 'Archived', colorClass: 'text-amber-600 dark:text-amber-400' },
    { state: 'deleted', label: 'Deleted', colorClass: 'text-red-600 dark:text-red-400' },
  ]

  return states.map(({ state, label, colorClass }) => {
    const stateSnapshots = tenantSnapshots.filter((s) => s.lifecycleState === state)
    const totalCount = stateSnapshots.reduce((sum, s) => sum + s.totalCount, 0)
    return { state, label, totalCount, colorClass }
  })
})
</script>

<template>
  <div v-if="visible" class="mb-6">
    <h3 class="mb-3 text-sm font-medium text-muted-foreground">Lifecycle State Breakdown</h3>
    <div class="grid grid-cols-3 gap-3">
      <Card v-for="item in breakdownItems" :key="item.state">
        <CardHeader class="pb-2">
          <CardTitle class="text-xs font-medium text-muted-foreground">
            {{ item.label }}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton v-if="loading" class="h-6 w-16" />
          <div v-else :class="['text-xl font-bold', item.colorClass]">
            {{ item.totalCount.toLocaleString() }}
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
</template>
